import { FileUpload, Refresh } from "@mui/icons-material";
import { Box, Button, Divider, Typography } from "@mui/material";
import { useFormikContext, Field, FieldProps } from "formik";
import { useSnackbar } from "notistack";
import { useState, useCallback, ReactElement } from "react";

import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import { FormikColorPickerField } from "~frontend/components/forms/FormikColorPickerField";
import { useMediaUpload } from "~frontend/hooks/useMediaUpload";
import { palette } from "~frontend/theme";

import { TemplatePreview } from "./TemplatePreview";
import { CreateOrEditGifFormValues } from "./types";

export function CustomizeDesignFields(): ReactElement {
  const form = useFormikContext<CreateOrEditGifFormValues>();

  const [previewCustomColors, setPreviewCustomColors] = useState(
    form.values.customColors
  );
  const [previewLogoUrl, setPreviewLogoUrl] = useState(form.values.logoUrl);
  const refreshPreview = useCallback(() => {
    setPreviewCustomColors(form.values.customColors);
    setPreviewLogoUrl(form.values.logoUrl);
  }, [form, setPreviewCustomColors, setPreviewLogoUrl]);

  return (
    <>
      <FlexBox
        flexDirection="column"
        alignItems="center"
        gap="24px"
        sx={{ marginBottom: "24px" }}
      >
        <TemplatePreview
          template={form.values.template!}
          customFields={form.values.customFields}
          customColors={previewCustomColors}
          logoUrl={previewLogoUrl}
        />
        <FlexBox alignItems="center" gap="8px" onClick={refreshPreview}>
          <Refresh />
          <LinkButton>Update Preview</LinkButton>
        </FlexBox>
      </FlexBox>
      <Divider sx={{ borderColor: palette.midGray, marginBottom: "24px" }} />
      <FlexBox flexWrap="wrap" gap="8px 24px" sx={{ marginBottom: "16px" }}>
        {form.values.template?.colorVariables.map(colorVariable => (
          <Box key={colorVariable.name} sx={{ width: "calc(50% - 12px)" }}>
            <FormikColorPickerField
              name={`customColors.${colorVariable.name}`}
              fullWidth
              label={colorVariable.friendlyName}
            />
          </Box>
        ))}
      </FlexBox>
      {form.values.template?.logoUrlKey ? (
        <LogoField />
      ) : (
        <Field name="logoUrl" type="hidden" value={null} />
      )}
    </>
  );
}

const MAX_LOGO_SIZE_KB = 500;
const LOGO_WIDTH_PX = 400;
const LOGO_HEIGHT_PX = 170;

function LogoField() {
  const snackbar = useSnackbar();

  const { uploadMedia } = useMediaUpload();

  const [uploading, setUploading] = useState(false);

  const handleUpload = useCallback(
    async (file: File): Promise<string | null> => {
      setUploading(true);

      if (file.size > MAX_LOGO_SIZE_KB * 1000) {
        snackbar.enqueueSnackbar(
          `File must be no larger than ${MAX_LOGO_SIZE_KB} KB`,
          { variant: "error" }
        );
        setUploading(false);
        return null;
      }

      const mediaUpload = await uploadMedia({ file });
      setUploading(false);
      if (!mediaUpload?.url) {
        snackbar.enqueueSnackbar(
          "Error uploading image file, please try again.",
          { variant: "error" }
        );
        return null;
      }
      return mediaUpload.url;
    },
    [setUploading, uploadMedia, snackbar]
  );

  return (
    <Field name="logoUrl">
      {({
        field,
        form,
      }: FieldProps<string | null, CreateOrEditGifFormValues>) => {
        if (uploading) {
          return <Loading />;
        }

        if (field.value) {
          return (
            <FlexBox flexDirection="column" alignItems="center" gap="16px">
              <Box
                component="img"
                src={field.value}
                sx={{
                  maxWidth: "200px",
                  maxHeight: "200px",
                  display: "block",
                  borderWidth: "1px",
                  borderStyle: "solid",
                  borderColor: palette.midGray,
                }}
              />
              <Button
                variant="contained"
                color="tertiary"
                onClick={() => form.setFieldValue(field.name, null)}
              >
                Remove Image File
              </Button>
            </FlexBox>
          );
        }

        return (
          <FlexBox flexDirection="column" alignItems="center" gap="16px">
            <Button
              variant="contained"
              color="tertiary"
              component="label"
              startIcon={<FileUpload />}
            >
              Upload Your Organization's Logo
              <input
                type="file"
                hidden
                accept="image/png"
                onChange={async event => {
                  event.preventDefault();
                  if (!event.target.files?.length) {
                    return;
                  }
                  const url = await handleUpload(event.target.files[0]);
                  await form.setFieldValue(field.name, url);
                }}
              />
            </Button>
            <Typography variant="body3" sx={{ color: palette.darkGray }}>
              Your logo file must be .png file format and below{" "}
              {MAX_LOGO_SIZE_KB} KB, and we recommend that it be {LOGO_WIDTH_PX}
              px by {LOGO_HEIGHT_PX}px,
            </Typography>
          </FlexBox>
        );
      }}
    </Field>
  );
}
